import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { markNav } from "../../lib/usageTracker";

/**
 * Ribbon — issue #1119 (D216, D221 O1, `Destinations4.dc.html`). The one
 * menu: a full-width navy band under the white top bar, items on one line
 * that run past the edge and scroll sideways, the current item a white pill
 * that scrolls itself into view on mount and whenever `currentKey` changes.
 * Items are 44px tall (`--nw-size-tap`, #1118, D221.3).
 *
 * `items = [{ key, label, to, testId }]`. The current item is a `<span
 * aria-current="page">`, a button only when `onCurrentClick` is supplied
 * (the same opt-in `DestinationRow` carried: Plan's `?new=1` screen has no
 * other way off it, #763 F1); every other item is a router `Link`. The
 * `cursor` key is only ever ADDED when interactive (#760 RB1).
 *
 * #1428 (D385): the ribbon is a full-width band at every width. Its items are inset by `--nw-band-gutter`
 * (20px on a phone; from md the room that puts the first item one gutter inside the left edge of the 1040px column), so the
 * ribbon does not jump between a narrow screen and a wide one. The band itself stays full width.
 */
const ITEM_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  minHeight: "var(--nw-size-tap)",
  padding: "0 12px",
  borderRadius: 6,
  whiteSpace: "nowrap",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  textDecoration: "none",
};

// A keyboard user who Tabs onto a half-visible item gets it brought whole
// into view (#1177 F1). No `behavior` is passed, so the scroll is instant
// unless the page asks otherwise: nothing here animates, which also keeps
// prefers-reduced-motion honest.
const revealOnFocus = (e) => {
  const el = e.currentTarget;
  if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ inline: "nearest", block: "nearest" });
};

export function Ribbon({ items, currentKey, onCurrentClick, className = "", "data-testid": testId = "nav-ribbon", ...rest }) {
  const currentRef = useRef(null);

  useEffect(() => {
    const el = currentRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ inline: "nearest", block: "nearest" });
    }
  }, [currentKey]);

  return (
    <div
      data-testid={testId}
      data-nav-menu="ribbon"
      // Scrollbar hidden the way `ui/tabs.jsx` hides it.
      className={`[-ms-overflow-style:none] [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden ${className}`.trim()}
      style={{
        display: "flex",
        flexWrap: "nowrap",
        gap: 8,
        padding: "0 var(--nw-band-gutter) 8px",
        background: "var(--nw-navy)",
        overflowX: "auto",
      }}
      {...rest}
    >
      {items.map((d) => {
        if (d.key === currentKey) {
          const interactive = typeof onCurrentClick === "function";
          return (
            <span
              key={d.key}
              ref={currentRef}
              data-testid={d.testId}
              aria-current="page"
              onFocus={revealOnFocus}
              onClick={interactive ? () => onCurrentClick(d.key) : undefined}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onCurrentClick(d.key);
                      }
                    }
                  : undefined
              }
              style={{ ...ITEM_BASE, background: "#FFFFFF", color: "var(--nw-navy)", ...(interactive ? { cursor: "pointer" } : {}) }}
            >
              {d.label}
            </span>
          );
        }
        return (
          <Link key={d.key} to={d.to} data-testid={d.testId} onFocus={revealOnFocus} onClick={() => markNav("destination_row")} style={{ ...ITEM_BASE, color: "rgba(255,255,255,0.8)" }}>
            {d.label}
          </Link>
        );
      })}
    </div>
  );
}

export default Ribbon;
